/**
 * useManagerIntelligenceStats Hook
 * 
 * Fetches manager intelligence metrics for the Manager Passport.
 * Respects visibility rules (Tweak #1): owners see all, self-view requires unlock.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
// DTC-L: route self-view-unlock comparison through clock.
import { clock } from '@/utils/clock';
import { resolveMembershipRoles } from '@/lib/roles/multiRole';
import { isElevatedAuthority } from '@/lib/roles/offeredRoles';

export interface ManagerIntelligenceStats {
  id: string;
  userId: string;
  shopId: string;
  periodStart: string;
  periodEnd: string;
  sampleCount: number;
  sampleStatus: 'learning' | 'established' | 'expert';
  // Intervention Effectiveness
  overrideFrequency: number | null;
  overrideImprovementRate: number | null;
  instinctAccuracy: number | null;
  // Load Management
  highLoadResponseScore: number | null;
  rolloverTrend: number | null;
  balanceEffectiveness: number | null;
  // Coaching Impact
  coachedTechImprovementRate: number | null;
  coachedAdvisorImprovementRate: number | null;
  mismatchReductionRate: number | null;
  // System Health
  parkingLotEfficiency: number | null;
  eodScheduleHealth: number | null;
  reworkTrend: number | null;
  // Composite
  systemStewardshipIndex: number | null;
  trendDirection: 'improving' | 'stable' | 'drifting' | null;
  // Visibility
  selfViewEnabled: boolean;
  selfViewUnlockedAt: string | null;
}

export interface ManagerInterventionEvent {
  id: string;
  managerUserId: string;
  shopId: string;
  ticketId: string | null;
  eventType: string;
  eventMetadata: Record<string, unknown>;
  detectedAt: string;
}

function transformStats(data: any): ManagerIntelligenceStats {
  return {
    id: data.id,
    userId: data.user_id,
    shopId: data.shop_id,
    periodStart: data.period_start,
    periodEnd: data.period_end,
    sampleCount: data.sample_count || 0,
    sampleStatus: data.sample_status || 'learning',
    overrideFrequency: data.override_frequency,
    overrideImprovementRate: data.override_improvement_rate,
    instinctAccuracy: data.instinct_accuracy,
    highLoadResponseScore: data.high_load_response_score,
    rolloverTrend: data.rollover_trend,
    balanceEffectiveness: data.balance_effectiveness,
    coachedTechImprovementRate: data.coached_tech_improvement_rate,
    coachedAdvisorImprovementRate: data.coached_advisor_improvement_rate,
    mismatchReductionRate: data.mismatch_reduction_rate,
    parkingLotEfficiency: data.parking_lot_efficiency,
    eodScheduleHealth: data.eod_schedule_health,
    reworkTrend: data.rework_trend,
    systemStewardshipIndex: data.system_stewardship_index,
    trendDirection: data.trend_direction,
    selfViewEnabled: data.self_view_enabled || false,
    selfViewUnlockedAt: data.self_view_unlocked_at,
  };
}

export function useManagerIntelligenceStats(userId: string | undefined) {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['manager-intelligence-stats', userId, activeShop?.id],
    queryFn: async () => {
      if (!userId || !activeShop?.id) return null;

      const { data, error } = await supabase
        .from('manager_intelligence_stats')
        .select('*')
        .eq('user_id', userId)
        .eq('shop_id', activeShop.id)
        .order('period_end', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return transformStats(data);
    },
    enabled: !!userId && !!activeShop?.id,
  });
}

export function useManagerInterventionEvents(userId: string | undefined, limit = 20) {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['manager-intervention-events', userId, activeShop?.id, limit],
    queryFn: async () => {
      if (!userId || !activeShop?.id) return [];

      const { data, error } = await supabase
        .from('manager_intervention_events')
        .select('*')
        .eq('manager_user_id', userId)
        .eq('shop_id', activeShop.id)
        .order('detected_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((event) => ({
        id: event.id,
        managerUserId: event.manager_user_id,
        shopId: event.shop_id,
        ticketId: event.ticket_id,
        eventType: event.event_type,
        eventMetadata: event.event_metadata as Record<string, unknown>,
        detectedAt: event.detected_at,
      }));
    },
    enabled: !!userId && !!activeShop?.id,
  });
}

/**
 * Check if the current user can view the manager's stats
 * (Tweak #1: Visibility rules - only shop owners can view manager stats)
 */
export function useCanViewManagerStats(managerUserId: string | undefined) {
  const { user, activeShop } = useAuth();
  
  return useQuery({
    queryKey: ['can-view-manager-stats', managerUserId, user?.id, activeShop?.id],
    queryFn: async () => {
      if (!managerUserId || !user?.id || !activeShop?.id) {
        return { canView: false, reason: 'missing_data' };
      }

      // MULTI-ROLE: this asked `memberships.role === 'MANAGER' | 'OWNER'`.
      //
      // That scalar used to carry authority. It does not any more: a member
      // created since the multi-role migration holds the NEUTRAL legacy value
      // `STAFF` with their real authority in `authority_role`, so an owner who
      // also turns wrenches — or any owner invited after the migration — read
      // as `STAFF` here and was refused access to a page they own. Authority
      // questions go through the canonical resolver, which is the one place
      // that knows how to read either shape.
      //
      // The SET is unchanged: OWNER and MANAGER, exactly as before.
      // `isElevatedAuthority` is that pair by definition; widening it to
      // ORG_ADMIN would be a product decision this repair was not given.
      const { data: membership } = await supabase
        .from('memberships')
        .select('role, authority_role')
        .eq('user_id', user.id)
        .eq('shop_id', activeShop.id)
        .eq('status', 'active')
        .single();

      if (isElevatedAuthority(resolveMembershipRoles(membership as never).authority)) {
        return { canView: true, reason: 'manager_or_owner', isSelfView: false };
      }

      // Check if viewing own stats
      if (user.id === managerUserId) {
        // Check self-view unlock status
        const { data: stats } = await supabase
          .from('manager_intelligence_stats')
          .select('self_view_enabled, self_view_unlocked_at')
          .eq('user_id', managerUserId)
          .eq('shop_id', activeShop.id)
          .order('period_end', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!stats?.self_view_enabled) {
          return { canView: false, reason: 'self_view_not_enabled', isSelfView: true };
        }

        if (!stats.self_view_unlocked_at || new Date(stats.self_view_unlocked_at) > clock.now()) {
          return { canView: false, reason: 'self_view_not_unlocked_yet', isSelfView: true };
        }

        return { canView: true, reason: 'self_view', isSelfView: true };
      }

      return { canView: false, reason: 'no_access' };
    },
    enabled: !!managerUserId && !!user?.id && !!activeShop?.id,
  });
}
